import { ExclamationCircleFilled } from '@ant-design/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Tooltip, Typography } from 'antd';
import moment from 'moment';
import { useCallback, useContext } from 'react';
import { useSearchParams } from 'react-router-dom';

import { HistoryActionItem } from '@app/components';
import type { NLogsGenerator } from '@app/components/LogsGenerator/LogsGenerator.types';
import { PREFIX, SYSTEM_USER_ID } from '@app/constants';
import { useUserPrint } from '@app/hooks';
import { NotificationContext } from '@app/providers';
import { useTaskListContext } from '@app/providers/TaskListProvider';
import { useLazyGetCrmTaskQuery } from '@app/services/rtkQuery/CrmTasks/api';
import type { NActionsHistory } from '@app/types/ActionsHistory';
import { formatActionText, generateKey } from '@app/utils';

import type { IconProp } from '@fortawesome/fontawesome-svg-core';

import '@app/components/LogsGenerator/styles/LogsGenerator.desktop.scss';
import '@app/components/LogsGenerator/styles/LogsGenerator.tablet.scss';

const { Text, Link } = Typography;

export const LogsGenerator: NLogsGenerator.Main = ({ actions, isEmployee }) => {
  const [getCrmTask] = useLazyGetCrmTaskQuery();
  const [, setSearchParams] = useSearchParams();
  const { activeBranchId } = useUserPrint();

  const { showNotification } = useContext(NotificationContext);
  const { openPreviewTaskModal } = useTaskListContext();

  const onOpenRecord = useCallback(
    (id: number) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          params.set('recordId', id.toString());
          return params;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  return (
    <div className={`${PREFIX}logs-generator`}>
      {actions.map(
        ({
          key,
          Timestamp,
          ActionId,
          Icon,
          ActionUserId,
          ActionUser,
          ActionText,
          ObjectS,
          ObjectId,
          ObjectType,
          IsImportantAction,
          ActionItems,
        }) => (
          <div
            className="action"
            data-testid="action"
            key={key || `${ActionId}-${Timestamp}`}
          >
            <Text>
              <FontAwesomeIcon
                icon={Icon as IconProp}
                size={'lg'}
                style={{ width: '30px', marginRight: '5px' }}
                data-testid={Icon || ''}
              />
              {ActionUserId === SYSTEM_USER_ID ? (
                <Text data-testid="user-name" strong>{`${ActionUser} `}</Text>
              ) : (
                <Link
                  data-testid="user-name"
                  strong
                  href={`/${
                    isEmployee ? 'employees' : 'users'
                  }/edit/?id=${ActionUserId}&key=1`}
                >
                  {`${ActionUser} `}
                </Link>
              )}
              {formatActionText({
                actionText: ActionText,
                objectS: ObjectS,
                objectType: ObjectType,
                objectId: ObjectId,
                getCrmTask,
                activeBranchId,
                showNotification,
                openPreviewTaskModal,
                onOpenRecord,
              })}
              {' - '}
              <span data-testid="action-info">
                {moment(Timestamp).format('DD.MM.YYYY, HH:mm:ss')}
              </span>
              {IsImportantAction && (
                <>
                  {'  '}
                  <Tooltip title="Важное действие">
                    <ExclamationCircleFilled
                      style={{ cursor: 'pointer', color: '#ff7875' }}
                      data-testid="important-action-icon"
                    />
                  </Tooltip>
                </>
              )}
            </Text>
            {ActionItems?.map(
              (actionItem: NActionsHistory.ModelActionHistoryItem, index) => (
                <HistoryActionItem
                  key={generateKey(
                    'LogsGenerator',
                    'action-item',
                    `${actionItem.Property}-${index}`
                  )}
                  index={index}
                  actionItem={actionItem}
                />
              )
            )}
          </div>
        )
      )}
    </div>
  );
};
